import React, { useContext, useState, useEffect } from 'react';
import './ContactForm.scss';
import icon from '../../assets/banner/icons/Calling.png';
import { LanguageContext } from '../../context/LanguageContext';
import emailjs from '@emailjs/browser';
import useAnalytics from '../../hooks/useAnalytics';

const ContactForm = () => {
    const { t, currentLang } = useContext(LanguageContext);
    const { 
        trackEvent,
        trackContactInteraction
    } = useAnalytics();
    const [formData, setFormData] = useState({
        email: '',
        service: 'İmplant',
        phone: '',
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
    const [errorDetails, setErrorDetails] = useState(''); // Hata detayları için state
    const [debugInfo, setDebugInfo] = useState(null); // Debug bilgileri için state
    const [formStarted, setFormStarted] = useState(false);

    // EmailJS yapılandırması - Ortam değişkenlerini kullan
    // Not: Bu değerler .env dosyasında tanımlanmalıdır
    const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    
    // Ortam değişkenlerinin tanımlı olup olmadığını kontrol et
    const isEmailJSConfigured = EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID;

    // Telefon numarası (boşluksuz format tel: protokolü için)
    const phoneNumber = t('appointment.contact.phone').replace(/\s/g, '');

    // Services bileşenindeki listeyle aynı tedavi listesi
    const services = [
        "Protez",
        "İmplant", 
        "Beyazlatma",
        "Kanal Tedavisi"
    ];

    // Basitleştirilmiş form görüntüleme eventi
    useEffect(() => {
        // Tek bir net event
        trackEvent('form_view', {
            form_type: 'appointment',
            source: 'contact_page'
        });
    }, [trackEvent]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Sadece ilk etkileşimde form_interaction eventi gönder
        if (!formStarted) {
            // Yeni basitleştirilmiş event - ilk etkileşim
            trackEvent('form_interaction', {
                field: name,
                source: 'appointment_form'
            });
            
            setFormStarted(true);
        }
        
        
        setFormData({
            ...formData,
            [name]: value
        });
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        // E-posta ya da telefon alanlarından en az biri dolu olmalı
        const hasEmail = formData.email && formData.email.trim() !== '';
        const hasPhone = formData.phone && formData.phone.trim() !== '';
        
        if (!hasEmail && !hasPhone) {
            newErrors.contact = 'E-posta adresi veya telefon numarasından en az birini girmelisiniz.';
        }
        
        // E-posta formatı kontrolü (eğer girilmişse)
        if (hasEmail) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = t('appointment.form.validation.emailInvalid');
            }
        }
        
        // Telefon formatı kontrolü (eğer girilmişse)
        if (hasPhone) {
            const phoneRegex = /^[+]?[\d\s\-()]{10,15}$/;
            if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
                newErrors.phone = t('appointment.form.validation.phoneInvalid');
            }
        }
        
        // Hizmet seçimi zorunlu
        if (!formData.service || formData.service.trim() === '') {
            newErrors.service = t('appointment.form.validation.serviceRequired');
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Basitleştirilmiş form gönderim eventi
        trackEvent('form_submitted', {
            service: formData.service,
            has_phone: !!formData.phone,
            has_email: !!formData.email,
            has_message: !!formData.message,
            source: 'appointment_form'
        });
        
        if (!validateForm()) {
            // Basitleştirilmiş form hata eventi
            trackEvent('form_error', {
                error_type: 'validation',
                fields: Object.keys(errors),
                source: 'appointment_form'
            });
            return;
        }
        
        setIsSubmitting(true);
        setSubmitStatus(null);
        setErrorDetails(''); // Hata detaylarını sıfırla
        setDebugInfo(null); // Debug bilgilerini sıfırla
        
        // EmailJS yapılandırması eksikse hata göster
        if (!isEmailJSConfigured) {
            console.error('EmailJS yapılandırması eksik. Lütfen .env dosyasını kontrol edin.');
            setSubmitStatus('error');
            setErrorDetails('E-posta gönderme servisi yapılandırılmamış. Lütfen site yöneticisiyle iletişime geçin.');
            setIsSubmitting(false);
            
            // Basitleştirilmiş form hata eventi
            trackEvent('form_error', {
                error_type: 'configuration',
                message: 'E-posta gönderme servisi yapılandırılmamış',
                source: 'appointment_form'
            });
            return;
        }
        
        // EmailJS için parametreleri hazırla
        const templateParams = {
            name: 'Randevu Talebi', // İsim kaldırıldığı için sabit değer
            email: formData.email || 'Belirtilmedi',
            service: formData.service,
            phone: formData.phone || 'Belirtilmedi',
            message: formData.message || 'Mesaj yazılmadı',
            language: currentLang,
            to_email: 'info@monaklinik.com,monaclinik@gmail.com,omer.aksu1@gmail.com' // Üç farklı e-posta adresine gönderim
        };
        
        console.log('Form gönderiliyor:', {
            ...templateParams,
            messageLength: formData.message.length
        });
        
        try {
            // EmailJS ile e-posta gönder
            console.log('EmailJS ile e-posta gönderme başlatıldı: Service ID:', EMAILJS_SERVICE_ID, 'Template ID:', EMAILJS_TEMPLATE_ID);
            
            // Gönderilecek e-posta adresleri
            console.log('Gönderilecek e-posta adresleri:', templateParams.to_email);
            
            // Tek seferde tek e-posta göndererek sorunun önüne geçiliyor
            const response = await emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                templateParams
            );
            
            console.log('E-posta başarıyla gönderildi:', response);
            
            // Yanıtı debug bilgisi olarak kaydet
            setDebugInfo({
                emailResponses: [{address: templateParams.to_email, response}]
            });
            
            setSubmitStatus('success');
            
            // Basitleştirilmiş form başarı eventi
            trackEvent('form_success', {
                service: formData.service,
                has_message: formData.message.length > 0,
                has_phone: !!formData.phone,
                has_email: !!formData.email,
                source: 'appointment_form'
            });
            
            // Google Ads Dönüşüm İzleme
            try {
                if (typeof window.sendGoogleAdsConversion === 'function') {
                    // Form dönüşümü için Google Ads dönüşüm gönder
                    window.sendGoogleAdsConversion(
                        process.env.REACT_APP_GOOGLE_ADS_ID,
                        'form_submission', // Conversion Label
                        100 // Varsayılan değer
                    );
                    console.log('Google Ads form dönüşümü gönderildi');
                } else if (typeof window.gtag === 'function') {
                    // Direkt olarak gtag fonksiyonunu kullan (yedek çözüm)
                    const adsId = process.env.REACT_APP_GOOGLE_ADS_ID;
                    if (adsId) {
                        window.gtag('event', 'conversion', {
                            'send_to': `${adsId}/form_submission`,
                            'value': 100,
                            'currency': 'TRY'
                        });
                        console.log('Google Ads form dönüşümü gtag ile gönderildi');
                    }
                } else {
                    console.warn('Google Ads dönüşüm izleme kullanılamıyor: sendGoogleAdsConversion veya gtag fonksiyonu bulunamadı');
                }
            } catch (convError) {
                console.error('Google Ads dönüşüm izleme hatası:', convError);
            }
            
            // Formu sıfırla
            setFormData({
                email: '',
                service: 'İmplant',
                phone: '',
                message: ''
            });
            setFormStarted(false);
        } catch (error) {
            console.error('E-posta gönderme hatası:', error);
            setSubmitStatus('error');
            setErrorDetails(error.text || error.message || 'Bilinmeyen hata');
            setDebugInfo({
                errorName: error.name,
                errorMessage: error.message,
                errorStack: error.stack ? error.stack.split('\n')[0] : null,
                emailjsParams: {
                    serviceId: EMAILJS_SERVICE_ID,
                    templateId: EMAILJS_TEMPLATE_ID,
                    toEmail: templateParams.to_email
                },
                templateParams: {
                    ...templateParams,
                    message: templateParams.message.length > 100 
                        ? templateParams.message.substring(0, 100) + '...' 
                        : templateParams.message
                }
            });
            
            // Form gönderim hatası eventi
            trackEvent('form_submission_error', {
                form_type: 'appointment',
                error_type: error.name || 'unknown',
                error_message: error.message || 'unknown'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePhoneClick = () => {
        // Telefon numarasına tıklama eventi
        trackContactInteraction('phone', t('appointment.contact.phone'));
        trackEvent('call_button_click', { source: 'appointment_form' });
    };

    return (
        <form className="contact-form" onSubmit={handleSubmit}>
            {/* Form başarı mesajı */}
            {submitStatus === 'success' && (
                <div className="alert alert-success mb-4" role="alert">
                    <p>{t('appointment.form.success')}</p>
                    
                    {/* Debug bilgisi - Sadece geliştirme modunda göster */}
                    {process.env.NODE_ENV === 'development' && debugInfo && debugInfo.emailResponses && (
                        <div className="email-debug-info mt-3">
                            <h6>E-posta Gönderim Sonuçları:</h6>
                            <ul className="list-unstyled">
                                {debugInfo.emailResponses.map((item, index) => (
                                    <li key={index}>
                                        <strong>{item.address}:</strong> 
                                        {item.response ? ' Başarılı ✓' : ' Başarısız ✗'}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
            
            {/* Form hata mesajı */}
            {submitStatus === 'error' && (
                <div className="alert alert-danger mb-4" role="alert">
                    <p>{t('appointment.form.error')}</p>
                    {errorDetails && <small className="d-block mt-1">{errorDetails}</small>}
                    
                    {/* Debug bilgisi - Sadece geliştirme modunda göster */}
                    {process.env.NODE_ENV === 'development' && debugInfo && (
                        <div className="error-debug-info mt-3">
                            <h6>Hata Detayları:</h6>
                            <ul className="list-unstyled">
                                <li><strong>Hata Adı:</strong> {debugInfo.errorName || 'Bilinmiyor'}</li>
                                <li><strong>Hata Mesajı:</strong> {debugInfo.errorMessage || 'Bilinmiyor'}</li>
                                {debugInfo.emailjsParams && (
                                    <>
                                        <li><strong>Service ID:</strong> {debugInfo.emailjsParams.serviceId}</li>
                                        <li><strong>Template ID:</strong> {debugInfo.emailjsParams.templateId}</li>
                                        <li><strong>Alıcılar:</strong> {debugInfo.emailjsParams.toEmail}</li>
                                    </>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            )}
            
            {/* Genel iletişim hatası - E-posta ya da telefon eksik */}
            {errors.contact && (
                <div className="alert alert-warning mb-4" role="alert">
                    <p>{errors.contact}</p>
                </div>
            )}
            
            <div className="row">
                <div className="col-lg-6">
                    <div className="form-group">
                        <label>{t('appointment.form.email.label')}</label>
                        <input 
                            type="email" 
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder={t('appointment.form.email.placeholder')} 
                            autoComplete="email"
                        />
                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="form-group">
                        <label>{t('appointment.form.phone.label')}</label>
                        <input 
                            type="tel" 
                            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder={t('appointment.form.phone.placeholder')} 
                            autoComplete="tel"
                        />
                        {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                    </div>
                </div>
                <div className="col-lg-12">
                    <div className="form-group">
                        <label>{t('appointment.form.service.label')} <span className="text-danger">*</span></label>
                        <select 
                            className={`form-control ${errors.service ? 'is-invalid' : ''}`}
                            name="service"
                            value={formData.service}
                            onChange={handleChange}
                        >
                            {services.map((service, index) => (
                                <option key={index} value={service}>{service}</option>
                            ))}
                        </select>
                        {errors.service && <div className="invalid-feedback">{errors.service}</div>}
                    </div>
                </div>
                <div className="col-lg-12">
                    <div className="form-group">
                        <label>{t('appointment.form.message.label')}</label>
                        <textarea 
                            className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder={t('appointment.form.message.placeholder')} 
                            rows="3"
                        ></textarea>
                        {errors.message && <div className="invalid-feedback">{errors.message}</div>}
                    </div>
                </div>

                <div className="col-lg-6">
                    <button 
                        type="submit" 
                        className="btn appointment-btn"
                        disabled={isSubmitting}
                        onClick={() => trackEvent('appointment_button_click')}
                    >
                        {isSubmitting ? 'Gönderiliyor...' : 'Randevu Talebi Gönder'}
                    </button>
                </div>
                <div className="col-lg-6">
                    <a href={`tel:${phoneNumber}`} className="appointment-call-wrapper" onClick={handlePhoneClick}>
                        <div className="appointment-call">
                            <div className='icon'>
                                <img src={icon} alt={t('altText.icon')} />
                            </div>
                            <div className='call-text'>
                                <p>{t('appointment.contact.title')}</p>
                                <h6>{t('appointment.contact.phone')}</h6>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
        </form>
    );
};

export default ContactForm;